import * as React from 'react';

import translate from '../../../../locale/translate';
import { renderEmpty } from '../../common/Render';
import HorizontalBarChart from '../../common/Chart/HorizontalBarChart';

import { IReportSettings } from '../../../../interfaces';
import ISettings from './ISettings';
import { IWidgetProps } from '../../StateWidgetContainer';

import { sortByKeyComparator } from '../../util/ArrayUtil';
import { getDisplayValue } from '../../util/StringUtil';
import { calcChildErrors } from '../../util/StateUtil';

interface IProps extends IWidgetProps {
    settings: ISettings;
}

const AVAILABLE_COLS = [
    'name',
    'alertsTotal',
    'alertsNode',
    'alertsChildren',
];

const COL_TITLES = {
    name: translate.gettext("Name"),
    alertsTotal: translate.gettext("Total alerts"),
    alertsNode: translate.gettext("Alerts of system"),
    alertsChildren: translate.gettext("Alerts of sensors / Sensorhubs"),
};

const COL_CLASSES = {
    alertsTotal: 'col-xs-2 text-right',
    alertsNode: 'col-xs-2 text-right',
    alertsChildren: 'col-xs-2 text-right',
};

export default class Widget extends React.Component<IProps, void> {
    static propTypes = {
        nodes: React.PropTypes.array.isRequired,
        states: React.PropTypes.object.isRequired,
        settings: React.PropTypes.object.isRequired,
        reportSettings: React.PropTypes.object.isRequired,
    };

    renderChart(containers, countMap) {
        const data = containers.filter(container => {
            return container.subtype > 0 && countMap[container.id].totalErrorCnt > 0;
        }).map(container => {
            return {
                x: container.name,
                y: countMap[container.id].totalErrorCnt,
            }
        }).sort(sortByKeyComparator('x'));

        return (
            <div key="chart" className="text-center">
                <HorizontalBarChart data={data} />
            </div>
        );
    }

    renderContainerColumnHead(name: string, settings: ISettings) {
        if (getDisplayValue(name, settings)) {
            return (
                <th key={name} className={COL_CLASSES[name]}>{COL_TITLES[name]}</th>
            );
        }

        return;
    }

    renderContainerColumn(name: string, counts, settings: ISettings) {
        if (getDisplayValue(name, settings)) {
            let value;

            switch (name) {
                case 'alertsTotal': value = counts.totalErrorCnt; break;
                case 'alertsNode': value = counts.nodeErrorCnt; break;
                case 'alertsChildren': value = counts.childErrorCnt; break;
            }

            return (
                <td key={name} className={COL_CLASSES[name]}>{value}</td>
            );
        }

        return;
    }

    renderContainerRow(container, counts, settings: ISettings) {
        return (
            <tr key={container.id}>
                <td>{container.name}</td>
                {AVAILABLE_COLS.map(name => {
                    return this.renderContainerColumn(name, counts, settings);
                })}
            </tr>
        )
    }

    renderTable(containers, nodes, states, countMap, settings: ISettings) {
        const containerRows = containers.map(container => {
            return this.renderContainerRow(container, countMap[container.id], settings);
        });

        return (
            <table key="table" className="table">
                <thead>
                    <tr>
                        <th key='name' className={COL_CLASSES['name']}>{COL_TITLES['name']}</th>
                        {AVAILABLE_COLS.map(name => {
                            return this.renderContainerColumnHead(name, settings);
                        })}
                    </tr>
                </thead>
                <tbody>
                    {containerRows}
                </tbody>
            </table>
        );
    }

    renderData(containers, nodes, states, settings: ISettings) {
        const countMap = {};

        containers.forEach((container) => {
            calcChildErrors(container, nodes, states, countMap);
        });

        const children = [];

        if (settings.displayChart) {
            children.push(this.renderChart(containers, countMap));
        }

        if (settings.displayTable) {
            children.push(this.renderTable(containers, nodes, states, countMap, settings));
        }

        return (
            <div>
                {children}
            </div>
        );
    }

    render() {
        const { states, settings, reportSettings } = this.props;
        let { nodes } = this.props;
        let renderedData;

        nodes = nodes || [];

        const containers = nodes.filter(node => {
            return node.type === 2;
        });

        if (nodes && nodes.length && containers && containers.length) {
            renderedData = this.renderData(containers, nodes, states, settings);
        } else {
            renderedData = renderEmpty();
        }

        return (
            <div>
                {renderedData}
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/widget/alerts/listAlertCount/Widget.tsx